import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientApi } from '../../../services/api';
import type { Client, PaginatedResponse } from '../../../types';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const SupplierList: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    is_active: 'true',
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    current_page: 1
  });
  const [pageSize, setPageSize] = useState(15);

  const loadSuppliers = async (page = 1, size = pageSize) => {
    try {
      setLoading(true);
      const params: {
        page?: number;
        page_size?: number;
        search?: string;
        client_type?: string;
        is_active?: boolean;
        ordering?: string;
      } = {
        page,
        page_size: size,
        search: searchTerm,
        client_type: 'supplier', // Filtrar apenas fornecedores
        ordering: '-created_at'
      };

      if (filters.is_active) {
        params.is_active = filters.is_active === 'true';
      }

      const response: PaginatedResponse<Client> = await clientApi.getClients(params);
      setSuppliers(response.results);
      setPagination({
        count: response.count,
        next: response.next,
        previous: response.previous,
        current_page: page
      });
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadSuppliers(1, pageSize);
  }, [searchTerm, filters, pageSize]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDeleteSupplier = async (supplierId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este fornecedor?')) {
      try {
        await clientApi.deleteClient(supplierId);
        loadSuppliers(pagination.current_page, pageSize);
      } catch (error) {
        console.error('Error deleting supplier:', error);
        alert('Erro ao excluir fornecedor. Verifique se não há contas vinculadas.');
      }
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Ativo
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Inativo
      </span>
    );
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '-';
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Fornecedores</h1>
          <p className="text-xs text-secondary-600">
            Gerencie os fornecedores cadastrados no sistema
          </p>
        </div>
        <Link to="/financeiro/fornecedores/novo" className="btn-primary flex items-center whitespace-nowrap">
          <UserPlusIcon className="h-4 w-4 mr-1.5" />
          Novo Fornecedor
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou CNPJ (min. 3 caracteres)..."
                className="input-field pl-10"
                value={searchInput}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              className="input-field"
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-secondary-600">Carregando fornecedores...</p>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-8">
            <UserPlusIcon className="h-12 w-12 text-secondary-400 mx-auto" />
            <p className="mt-2 text-secondary-600">Nenhum fornecedor encontrado</p>
            <Link to="/financeiro/fornecedores/novo" className="mt-4 inline-block text-primary-600 hover:text-primary-800">
              Cadastrar primeiro fornecedor
            </Link>
          </div>
        ) : (
          <>
            {/* Responsive Cards for Mobile */}
            <div className="block lg:hidden space-y-3">
              {suppliers.map((supplier) => (
                <div key={supplier.id} className="border border-secondary-200 rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-secondary-900">{supplier.name}</h3>
                      <p className="text-xs text-secondary-500">{formatCNPJ(supplier.cpf_cnpj)}</p>
                    </div>
                    {getStatusBadge(supplier.is_active)}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-secondary-600 mb-2">
                    <div><span className="font-medium">Email:</span> {supplier.email || '-'}</div>
                    <div><span className="font-medium">Tel:</span> {supplier.phone || '-'}</div>
                    <div className="col-span-2"><span className="font-medium">Cidade:</span> {supplier.city && supplier.state ? `${supplier.city}/${supplier.state}` : '-'}</div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Link to={`/financeiro/fornecedores/${supplier.id}/editar`} className="text-yellow-600 hover:text-yellow-900 p-1" title="Editar">
                      <PencilIcon className="h-4 w-4" />
                    </Link>
                    <button onClick={() => handleDeleteSupplier(supplier.id)} className="text-red-600 hover:text-red-900 p-1" title="Excluir">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Table for Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Fornecedor</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">CNPJ</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Contato</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Cidade/UF</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Acoes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-secondary-25">
                      <td className="px-2 py-1.5">
                        <div className="text-xs font-medium text-secondary-900 truncate max-w-48">{supplier.name}</div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-secondary-900">{formatCNPJ(supplier.cpf_cnpj)}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-xs text-secondary-900">{supplier.email || '-'}</div>
                        <div className="text-xs text-secondary-500">{supplier.phone || '-'}</div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-secondary-900">
                        {supplier.city && supplier.state ? `${supplier.city}/${supplier.state}` : '-'}
                      </td>
                      <td className="px-2 py-1.5">{getStatusBadge(supplier.is_active)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex justify-end space-x-1">
                          <Link to={`/financeiro/fornecedores/${supplier.id}/editar`} className="text-yellow-600 hover:text-yellow-900 p-0.5" title="Editar">
                            <PencilIcon className="h-3.5 w-3.5" />
                          </Link>
                          <button onClick={() => handleDeleteSupplier(supplier.id)} className="text-red-600 hover:text-red-900 p-0.5" title="Excluir">
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.count > pageSize && (
              <div className="flex items-center justify-between border-t border-secondary-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => loadSuppliers(pagination.current_page - 1, pageSize)}
                    disabled={!pagination.previous}
                    className="relative inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadSuppliers(pagination.current_page + 1, pageSize)}
                    disabled={!pagination.next}
                    className="relative ml-3 inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Proximo
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-xs text-secondary-700">
                      Mostrando{' '}
                      <span className="font-medium">
                        {(pagination.current_page - 1) * pageSize + 1}
                      </span>{' '}
                      ate{' '}
                      <span className="font-medium">
                        {Math.min(pagination.current_page * pageSize, pagination.count)}
                      </span>{' '}
                      de{' '}
                      <span className="font-medium">{pagination.count}</span>{' '}
                      resultados
                    </p>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-secondary-700">Por pagina:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setPageSize(newSize);
                          loadSuppliers(1, newSize);
                        }}
                        className="text-sm border border-secondary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => loadSuppliers(pagination.current_page - 1, pageSize)}
                        disabled={!pagination.previous}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        ←
                      </button>
                      <button
                        onClick={() => loadSuppliers(pagination.current_page + 1, pageSize)}
                        disabled={!pagination.next}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Proximo</span>
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupplierList;
